import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Carousel as CarouselAntd } from "antd";
import { CustomArrowProps } from "@ant-design/react-slick";
import { CarouselProps } from "@companion-core/shared/app/Components/Carousel/Carousel.model";
import { useNeutralizeInactiveSlides } from "@companion-core/shared/app/Components/Carousel/useNeutralizeInactiveSlides";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import "@companion-core/web/src/Assets/Styles/scss/carousel.scss";

type DotElement = React.ReactElement<{
  className?: string;
  children: React.ReactElement<Record<string, unknown>>;
}>;

export const Carousel = ({
  children,
  autoplay = true,
  duration = 8000,
  testID,
  hasMultipleImages = true,
}: CarouselProps) => {
  const { t } = useTranslation();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const slideCount = React.Children.count(children);

  useNeutralizeInactiveSlides(containerRef);

  const PrevArrow = ({ className, style, onClick }: CustomArrowProps) => (
    <button
      type="button"
      className={className}
      style={style}
      onClick={onClick}
      aria-label={t("carousel.accessibility.previousSlide")}>
      <span className="circle" data-testid="carousel__left-arrow" aria-hidden="true">
        <FontIcon name="JP-Arrow-left-bold" size={16} aria-hidden="true" />
      </span>
    </button>
  );

  const NextArrow = ({ className, style, onClick }: CustomArrowProps) => (
    <button
      type="button"
      className={className}
      style={style}
      onClick={onClick}
      aria-label={t("carousel.accessibility.nextSlide")}>
      <span className="circle" data-testid="carousel__right-arrow" aria-hidden="true">
        <FontIcon name="JP-Arrow-right-bold" size={16} aria-hidden="true" />
      </span>
    </button>
  );

  const renderDot = (index: number) => (
    <button
      type="button"
      aria-label={t("carousel.accessibility.goToSlide", {
        current: index + 1,
        total: slideCount,
      })}
    />
  );

  const appendDots = (dots: React.ReactNode) => (
    <ul>
      {React.Children.map(dots, (dot) => {
        if (!React.isValidElement(dot)) return dot;
        const typedDot = dot as DotElement;
        const isActive = typedDot.props.className?.split(" ").includes("slick-active") ?? false;
        const dotButton = React.cloneElement(typedDot.props.children, {
          "aria-current": isActive ? "true" : undefined,
        });
        return React.cloneElement(typedDot, undefined, dotButton);
      })}
    </ul>
  );

  return (
    <div data-testid={testID} ref={containerRef}>
      <CarouselAntd
        dots={hasMultipleImages}
        draggable={hasMultipleImages}
        autoplay={autoplay}
        autoplaySpeed={duration}
        arrows={!isMobileSize && hasMultipleImages}
        prevArrow={<PrevArrow />}
        nextArrow={<NextArrow />}
        customPaging={renderDot}
        appendDots={appendDots}
        effect="scrollx">
        {children}
      </CarouselAntd>
    </div>
  );
};
